import React, { Fragment, useEffect, useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  OutlinedInput,
  Chip, Tooltip,
} from "@mui/material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import { useMutation, useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshingIndicator from "../components/RefreshingIndicator";
import NotListedLocationIcon from "@mui/icons-material/NotListedLocation";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import RenderOnRole from "./RenderOnRole";
import UserService from "../services/UserService";
import MarkAsLostRequestCardsDialog from "./MarkAsLostRequestCardsDialog";
import DownloadIcon from "@mui/icons-material/Download";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import Labels from "../labels/Labels";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

export default function AvailableCards(props) {
  const navigate = useNavigate();
  const {
    isLoading,
    isFetching,
    isError,
    data,
    dataLocations,
    isLoadingLocations,
    refetchFunction,
    refetchStatsFunction,
    setPageFunction,
    setFilterStringFunction,
    dataCount,
    isLoadingDataCount,
    dataPayCardAsReplacement,
    isLoadingPayCardAsReplacement,
    pageSize,
    onPageSizeChange,
    setLocationIdsFunction,
  } = props;

  const theme = useTheme();

  const [elements, setElements] = React.useState([]);

  const [select, setSelection] = useState([]);
  const [locationIds, setLocationIds] = React.useState({});

  const [selectedLocation, setSelectedLocation] = useState(0);
  const [selectedProductType, setSelectedProductType] = useState("");

  const [openMarkAsLostDlg, setOpenMarkAsLostDlg] = useState(false);
  const [infoToMarkAsLost, setInfoToMarkAsLost] = useState({});

  const [filter, setFilter] = useState("");

  // Fetch card product attributes
  async function fetchEmployerAttribute(attributeName) {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/attributes/${attributeName}`
    );
    return response;
  }

  const { data: usePaycardAttr } = useQuery(["usePaycardAttr"], () =>
    fetchEmployerAttribute("USE_PAYCARD")
  );

  const { data: useCorporateExpenseAttr } = useQuery(
    ["useCorporateExpenseAttr"],
    () => fetchEmployerAttribute("USE_CORPORATE_EXPENSE")
  );

  const { data: useIncentiveAttr } = useQuery(["useIncentiveAttr"], () =>
    fetchEmployerAttribute("USE_INCENTIVE")
  );

  const { data: useVirtualCardAttr } = useQuery(["useVirtualCardAttr"], () =>
    fetchEmployerAttribute("USE_VIRTUAL")
  );

  const { data: useAnonymousAttr } = useQuery(["useAnonymousAttr"], () =>
    fetchEmployerAttribute("USE_REWARDS_ANONYM")
  );

  const onFilterChange = (filter, params) => {
    const filterString = filter.quickFilterValues.join(" ");
    if (filterString === undefined) {
      setFilterStringFunction("");
      setFilter("");
    } else {
      setFilterStringFunction(filterString);
      setFilter(filterString);
    }
    return filter;
  };

  // Get available product types based on employer attributes
  const getAvailableProductTypes = () => {
    const productTypes = [];

    // Default product types
    const defaultProductTypes = [
      {
        id: "PAYCARD",
        name: Labels.getLabel("availableCards.productPaycard", "Paycard"),
        icon: <CreditCardIcon />,
        description: Labels.getLabel(
          "availableCards.paycardDesc",
          "Standard paycard for payroll distribution"
        ),
      },
      {
        id: "CORPORATE_EXPENSE",
        name: Labels.getLabel(
          "availableCards.productCorporate",
          "Corporate Expense"
        ),
        icon: <AccountBalanceWalletIcon />,
        description: Labels.getLabel(
          "availableCards.corporateDesc",
          "Card for managing corporate expenses"
        ),
      },
      {
        id: "INCENTIVE",
        name: Labels.getLabel("availableCards.productIncentive", "Incentive"),
        icon: <CardGiftcardIcon />,
        description: Labels.getLabel(
          "availableCards.incentiveDesc",
          "Rewards and incentives program cards"
        ),
      },
      {
        id: "REWARDS_ANONYM",
        name: Labels.getLabel("availableCards.productAnonymous", "Anonymous"),
        icon: <NotListedLocationIcon />,
        description: Labels.getLabel(
          "availableCards.anonymousDesc",
          "Anonymous reward cards"
        ),
      },
    ];

    // Add "All Products" option
    productTypes.push({
      id: "",
      name: Labels.getLabel("availableCards.allProducts", "All Products"),
      icon: null,
      description: Labels.getLabel(
        "availableCards.allProductsDesc",
        "Show cards of all product types"
      ),
    });

    // Get attribute values from individual queries
    const usePaycard = usePaycardAttr?.data?.value === "true";
    const useCorporateExpense = useCorporateExpenseAttr?.data?.value === "true";
    const useIncentive = useIncentiveAttr?.data?.value === "true";
    const useAnonymous = useAnonymousAttr?.data?.value === "true";

    // Check if any product attributes are defined
    const hasProductAttributes =
      (usePaycard !== undefined && usePaycard === true) ||
      (useCorporateExpense !== undefined && useCorporateExpense === true) ||
      (useIncentive !== undefined && useIncentive === true) ||
      (useAnonymous !== undefined && useAnonymous === true);

    if (!hasProductAttributes) {
      // If no attributes are defined, return all product types
      return [...productTypes, ...defaultProductTypes];
    }

    // Add products based on attribute values
    if (usePaycard) {
      productTypes.push(defaultProductTypes.find((p) => p.id === "PAYCARD"));
    }

    if (useCorporateExpense) {
      productTypes.push(
        defaultProductTypes.find((p) => p.id === "CORPORATE_EXPENSE")
      );
    }

    if (useIncentive) {
      productTypes.push(defaultProductTypes.find((p) => p.id === "INCENTIVE"));
    }

    if (useAnonymous) {
      productTypes.push(
        defaultProductTypes.find((p) => p.id === "REWARDS_ANONYM")
      );
    }

    return productTypes;
  };

  const onAssignButtonClick = (e, params) => {
    navigate("/assignCard/" + params.id);
  };

  const clearSelectionsAndRefreshData = () => {
    setSelectedLocation("");
    setSelection([]);
    refetchFunction();
    refetchStatsFunction();
    setFeedback({ open: false });
  };

  const changeLocationMutation = useMutation(
    (payCards) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/paycards/location`,
        { cards: payCards, location: payCards.location }
      );
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "availableCards.cardsLocationChanged",
            "cards location changed succesfully"
          ),
          onClose: () => {
            clearSelectionsAndRefreshData();
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onTransferButtonClick = (e, params, changeLocationMutation) => {
    let paycards = params;
    paycards.location = selectedLocation;
    changeLocationMutation.mutateAsync(paycards);
  };

  const onMarkAsLostButtonClick = (e, params) => {
    let paycards = params;
    setInfoToMarkAsLost(paycards);
    setOpenMarkAsLostDlg(true);
  };

  const onClearSelectionButtonClick = (e, params) => {
    setSelection([]);
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  var dataColumns = [
    { field: "id", headerName: "ID", width: 90, hidden: true, sortable: false },
    {
      field: "cardNumber",
      headerName: Labels.getLabel("availableCards.cardLastFour", "Card Last 4"),
      width: 120,
      sortable: false,
      hidden: dataPayCardAsReplacement?.data?.value === "true",
    },
    {
      field: "embossing",
      headerName: Labels.getLabel(
        "availableCards.embossingLine",
        "Embossing Line"
      ),
      width: 260,
      sortable: false,
      renderCell: (params) => {
        const cardType = params.row.cardType;
        if (cardType === "REWARDS_ANONYM") {
          return "ANONYMOUS CARD";
        }

        return (
          <Box>
            <div style={{ fontSize: "0.85em" }}>
              <span style={{ color: "gray" }}>{params.row.embossing}</span>
            </div>
          </Box>
        );
      },
    },
    {
      field: "cardProxy",
      headerName: Labels.getLabel("availableCards.proxy", "Proxy"),
      width: 150,
      sortable: false,
      hidden: dataPayCardAsReplacement?.data?.value === "true",
    },
    {
      field: "locationDescription",
      headerName: Labels.getLabel("availableCards.location", "Location"),
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const locationDescription = params.value;
        const departmentName = params.row.departmentName;
        //const tooltipTitle = departmentName;

        const tooltipTitle = (
            <Box>
              <div>{locationDescription}</div>
              {departmentName && (
                  <div><strong>{ Labels.getLabel("availableCards.requestedBy", "Requested by:") }</strong> {departmentName}</div>
              )}
            </Box>
        );
        return (
            <Tooltip title={tooltipTitle} arrow>
              <Box>
                <div>{locationDescription}</div>
                {departmentName && (
                    <div style={{ fontSize: "0.85em" }}>
                      <span style={{ color: "gray" }}><strong>{ Labels.getLabel("availableCards.requestedBy", "Requested by:") }</strong>  {departmentName}</span>
                    </div>
                )}
              </Box>
            </Tooltip>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel("availableCards.status", "Status"),
      width: 200,
      sortable: false,
    },
    {
      field: "cardType",
      headerName: Labels.getLabel("availableCards.productType", "Product Type"),
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const cardType = params.value || "PAYCARD"; // Default to PAYCARD if not specified
        const iconMap = {
          PAYCARD: <CreditCardIcon color="primary" />,
          CORPORATE_EXPENSE: <AccountBalanceWalletIcon color="primary" />,
          INCENTIVE: <CardGiftcardIcon color="primary" />,
          VIRTUAL: <PhoneIphoneIcon color="primary" />,
          REWARDS_ANONYM: <NotListedLocationIcon color="primary" />,
        };

        const productNames = {
          PAYCARD: Labels.getLabel("availableCards.productPaycard", "Paycard"),
          CORPORATE_EXPENSE: Labels.getLabel(
            "availableCards.productCorporate",
            "Corporate"
          ),
          INCENTIVE: Labels.getLabel(
            "availableCards.productIncentive",
            "Incentive"
          ),
          VIRTUAL: Labels.getLabel("availableCards.productVirtual", "Virtual"),
          REWARDS_ANONYM: Labels.getLabel(
            "availableCards.productAnonymous",
            "Anonymous"
          ),
        };

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {iconMap[cardType]}
            <span>{productNames[cardType] || cardType}</span>
          </Box>
        );
      },
    },
    {
      field: "Action",
      headerName: Labels.getLabel("availableCards.action", "Action"),
      width: 110,
      sortable: false,
      renderCell: (params) => {
        if (!select || select.length === 0)
          return (
            // you will find row info in params
            <RenderOnRole roles={["pc-assign-employee"]}>
              {!params.row.inventoryLocked && (
                <Button
                  onClick={(e) => onAssignButtonClick(e, params.row)}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    fontSize: "1 rem",
                    fontWeight: "bold",
                  }}
                >
                  <AssignmentIndIcon />
                  {Labels.getLabel("availableCards.assign", "Assign")}
                </Button>
              )}
            </RenderOnRole>
          );
        else return "";
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel("availableCards.updateDate", "Update Date"),
      width: 190,
      sortable: false,
    },
  ];

  const handleRowSelection = (rows) => {
    setSelection(rows);
  };

  const handleChange = (event, location) => {
    setSelectedLocation(location.props.value);
  };

  const handleChangeElements = (event) => {
    const {
      target: { value },
    } = event;
    setLocationIdsFunction(value);
    setLocationIds(value);
    //refetchFunction();
    setElements(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleProductTypeChange = (event) => {
    const newProductType = event.target.value;
    setSelectedProductType(newProductType);

    // You'll need to modify your backend to support filtering by product type
    // For now, we can add it to the filter string
    if (newProductType) {
      // Add the product type to the filter query
      // This assumes your backend will handle parsing this format
      const currentFilter = filter ? filter + " " : "";
      setFilterStringFunction(currentFilter + "&productType=" + newProductType);
    } else {
      // If "All Products" is selected, just use the current text filter
      setFilterStringFunction(filter);
    }

    // Refresh the data
    refetchFunction();
  };

  const downloadAvailableMutation = useMutation(
    (params) => {
      const locations =
        Array.from(locationIds).length > 0
          ? Array.from(locationIds).join(",")
          : null;

      // Add product type to the download request if selected
      const productTypeParam = selectedProductType
        ? `&productType=${selectedProductType}`
        : "";

      return HttpService.getAxiosClient().get(
        window.API_URL +
          `/paycards/available/export?filter=` +
          filter +
          (locations !== null ? `&locations=` + locations : "") +
          productTypeParam,
        { responseType: "blob" }
      );
    },
    {
      onSuccess: (response) => {
        const { headers, data } = response;
        const disposition = headers["content-disposition"];
        let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
        if (filename.toLowerCase().startsWith("utf-8''"))
          filename = decodeURIComponent(filename.replace("utf-8''", ""));
        else filename = filename.replace(/['"]/g, "");
        let url = window.URL.createObjectURL(data);
        let a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a); // append the element to the dom
        a.click();
        a.remove(); // afterwards, remove the element
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "availableCards.fileDownloadSuccess",
            "The file was download succesfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        if (err?.response?.status === 500) {
          setFeedback({
            open: true,
            type: "error",
            message: Labels.getLabel(
              "availableCards.errorDownloadingFile",
              "There was an error trying to download the file"
            ),
          });
        } else {
          const msg = err?.response?.data ? err.response.data : err.message;
          setFeedback({
            open: true,
            type: "error",
            message: msg,
          });
        }
      },
    }
  );

  const onDownloadDetailsButtonClick = (e, params) => {
    downloadAvailableMutation.mutate(null);
  };

  useEffect(() => {}, [select]);

  return (
    <Fragment>
      <div style={{ display: "flex" }}>
        <div style={{ marginRight: "20px" }}>
          <h1>{Labels.getLabel("availableCards.title", "Cards available")}</h1>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              style={{ margin: "10px", height: "40px" }}
              onClick={onDownloadDetailsButtonClick}
              variant="contained"
              disabled={downloadAvailableMutation.isLoading}
              color={
                downloadAvailableMutation.isLoading ? "secondary" : "primary"
              }
            >
              <DownloadIcon style={{ paddingRight: "5px" }} />
              {downloadAvailableMutation.isLoading
                ? Labels.getLabel(
                    "availableCards.downloading",
                    "Downloading..."
                  )
                : Labels.getLabel(
                    "availableCards.exportAvailableCards",
                    "Export Available Cards"
                  )}
            </Button>
          </div>
        </div>
        {isFetching ? <RefreshingIndicator /> : ""}
      </div>

      {isLoading || isLoadingDataCount || isLoadingPayCardAsReplacement ? (
        <CircularProgress color="inherit" />
      ) : !isError ? (
        <div style={{ height: 450, width: "100%" }}>
          <RenderOnRole roles={["pc-reassign-location"]}>
            {select &&
            select.length > 0 &&
            !isLoadingLocations &&
            dataLocations &&
            dataLocations.data.length > 0 &&
            !isLoadingPayCardAsReplacement &&
            dataPayCardAsReplacement ? (
              <Fragment>
                <FormControl style={{ width: "200px", height: "80px" }}>
                  <InputLabel id="simple-select-label">
                    {Labels.getLabel(
                      "availableCards.locationLabel",
                      "Location"
                    )}{" "}
                  </InputLabel>
                  <Select
                    labelId="simple-select-label"
                    id="simple-select"
                    label={Labels.getLabel(
                      "availableCards.locationLabel",
                      "Location"
                    )}
                    onChange={handleChange}
                    value={selectedLocation}
                  >
                    <MenuItem value={0}>-</MenuItem>
                    {dataLocations?.data.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  style={{ margin: "10px" }}
                  onClick={(e) =>
                    onTransferButtonClick(e, select, changeLocationMutation)
                  }
                  variant="contained"
                  disabled={
                    selectedLocation === 0 ||
                    changeLocationMutation.isLoading ||
                    feedback.open
                  }
                >
                  <EditLocationIcon />
                  <span style={{ paddingLeft: "10px" }}>
                    {!changeLocationMutation.isLoading
                      ? Labels.getLabel(
                          "availableCards.changeLocation",
                          " Change Location"
                        )
                      : Labels.getLabel(
                          "availableCards.processing",
                          "Processing..."
                        )}
                  </span>
                </Button>
                <Button
                  color="warning"
                  style={{ margin: "10px" }}
                  onClick={(e) => onMarkAsLostButtonClick(e, select)}
                  variant="contained"
                  disabled={feedback.open}
                >
                  <CreditCardOffIcon />
                  <span style={{ paddingLeft: "10px" }}>
                    {Labels.getLabel(
                      "availableCards.reportAsLost",
                      " REPORT AS LOST"
                    )}
                  </span>
                </Button>
                <Button
                  color="primary"
                  style={{ marginLeft: "40px", margin: "10px" }}
                  onClick={(e) => onClearSelectionButtonClick(e, select)}
                  variant="contained"
                  disabled={feedback.open}
                >
                  <IndeterminateCheckBoxIcon />
                  <span style={{ paddingLeft: "10px" }}>
                    {Labels.getLabel(
                      "availableCards.clearSelection",
                      " Clear the selection"
                    )}
                  </span>
                </Button>
              </Fragment>
            ) : (
              <div style={{ padding: "20px" }}>
                <span>
                  {Labels.getLabel(
                    "availableCards.clickCheckboxes",
                    "Click on the checkboxes to change the location"
                  )}{" "}
                </span>
                <NotListedLocationIcon color="primary" />
                <span>
                  {" "}
                  {Labels.getLabel(
                    "availableCards.orReportAsLost",
                    "or report as lost"
                  )}{" "}
                </span>
                <CreditCardOffIcon color="warning" />
              </div>
            )}
          </RenderOnRole>
          <div
            style={{
              textAlign: "right",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {/* Product Type Filter */}
            <FormControl
              sx={{
                m: 1,
                width: 250,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <InputLabel id="product-type-label">
                {Labels.getLabel(
                  "availableCards.productTypeLabel",
                  "Product Type"
                )}
              </InputLabel>
              <Select
                labelId="product-type-label"
                id="product-type-select"
                value={selectedProductType}
                onChange={handleProductTypeChange}
                input={
                  <OutlinedInput
                    label={Labels.getLabel(
                      "availableCards.productTypeLabel",
                      "Product Type"
                    )}
                  />
                }
              >
                {getAvailableProductTypes().map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    style={getStyles(option.name, [selectedProductType], theme)}
                  >
                    {option.icon && (
                      <Box
                        component="span"
                        sx={{
                          mr: 1,
                          display: "inline-flex",
                          verticalAlign: "middle",
                        }}
                      >
                        {React.cloneElement(option.icon, { color: "primary" })}
                      </Box>
                    )}
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Locations Filter */}
            <FormControl
              sx={{
                m: 1,
                width: 500,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <InputLabel id="demo-multiple-chip-label">
                {Labels.getLabel("availableCards.locationsLabel", "Locations")}
              </InputLabel>
              <Select
                //disabled={disableGroupOptions}
                labelId="demo-multiple-chip-label"
                id="demo-multiple-chip"
                multiple
                value={elements}
                onChange={handleChangeElements}
                input={
                  <OutlinedInput
                    id="select-multiple-chip"
                    label={Labels.getLabel(
                      "availableCards.locationsChipLabel",
                      "LocationsChip"
                    )}
                  />
                }
                renderValue={(selected, other) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((key) => {
                      return (
                        <Chip
                          key={key}
                          label={
                            dataLocations?.data.find((item) => item.id === key)
                              .description
                          }
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {dataLocations?.data?.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    style={getStyles(option.description, elements, theme)}
                  >
                    {option.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <DataGrid
            checkboxSelection={UserService.hasRole(["pc-reassign-location"])}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            columnVisibilityModel={{ id: false }}
            onSelectionModelChange={handleRowSelection}
            onPageSizeChange={onPageSizeChange}
            selectionModel={select}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 800 },
              },
            }}
            rows={data?.data}
            columns={[
              {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                headerName: Labels.getLabel(
                  "availableCards.changeLocation",
                  "Change Location"
                ),
                width: 50,
              },
              ...dataColumns.filter((item) => item.hidden !== true),
            ]}
            rowCount={dataCount?.data}
            onPageChange={(newPage) => {
              setPageFunction(newPage);
            }}
            paginationMode="server"
            filterMode="server"
            onFilterModelChange={onFilterChange}
            keepNonExistentRowsSelected
            sx={{
              height: "calc(55vh)",
              "& .MuiButton-text ": { display: "none" },
              "& .MuiDataGrid-cellContent": {
                fontFamily: "Helvetica",
                fontSize: "1rem",
              },
            }}
          />
          <Snackbar
            open={feedback.open}
            autoHideDuration={6000}
            onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={
                feedback.onClose ? feedback.onClose : handleFeedbackClose
              }
              severity={feedback.type}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          </Snackbar>
          {openMarkAsLostDlg ? (
            <MarkAsLostRequestCardsDialog
              action="REPORT AS LOST"
              open={openMarkAsLostDlg}
              setOpen={setOpenMarkAsLostDlg}
              dialogTitle="Report As Lost Request"
              dialogContentText={Labels.getLabel(
                "availableCards.reportAsLostConfirm",
                "Are you sure you want to report the selected {0} card(s) as lost?, Please type the reason and click REPORT AS LOST button."
              ).replace("{0}", infoToMarkAsLost.length)}
              infoToProcess={infoToMarkAsLost}
              clearFunction={clearSelectionsAndRefreshData}
            />
          ) : (
            ""
          )}
        </div>
      ) : (
        <h4>
          {Labels.getLabel(
            "availableCards.errorOccurred",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
    </Fragment>
  );
}
